import { logUserInteraction } from "~vue/utils/logUtils";
import maxBy from "lodash/maxBy";
import mitt from "mitt";

/**
 * Unless otherwise specified in the constructor, this is how frequently to check the current audio
 * level, in millisconds.
 */
const DEFAULT_LEVEL_CHECK_INTERVAL_MS = 200;

/**
 * Unless otherwise specified in the constructor, this the audio level threshold above which a given
 * recording will no longer be considered "silence".  Expressed as a number between 0 and 1.
 */
const DEFAULT_SILENCE_THRESHOLD = 0.1;

/**
 * Unless otherwise specified in the constructor, the 'silence' event will be triggered after this
 * many seconds of silence from the time the recording is started.
 */
const DEFAULT_SILENCE_TIMEOUT_SECS = 5;

/** Minimal percentage of audible audio in recorded sample. 1.5% can be sufficient signal. A very paused speech will average 5-10%. */
const DEFAULT_AUDIBLE_THRESHOLD_PERCENTAGE = 0.015;

/**
 * Unless otherwise specified in the constructor, recordings will stop automatically after this many
 * seconds have elapsed.
 */
const DEFAULT_MAX_RECORDING_TIME_SECS = 60 * 10;

/** Audio bitrate for Opus codec. 64kbps balances quality and file size for Whisper transcription. */
const AUDIO_BITS_PER_SECOND = 64000;

/** Preferred MIME type for audio recording. */
const PREFERRED_MIME_TYPE = "audio/webm;codecs=opus";

/** Maximum time to wait for the audio stream to deliver non-zero samples after recording starts. */
const DEFAULT_WARMUP_TIMEOUT_MS = 3000;

/** Minimum audio level to consider the stream as delivering real audio (not dead air). */
const WARMUP_LEVEL_THRESHOLD = 0.01;

/** Timeout for getUserMedia initialization. Generous to support Citrix/AVD environments. */
const DEFAULT_INIT_TIMEOUT_MS = 45000;

/** Log a slow initialization event if getUserMedia takes longer than this. */
const DEFAULT_SLOW_INIT_THRESHOLD_MS = 3000;

/** Converts a 0-255 uint8 level centered on 127 to a 0-128 absolute value level. */
const absLevel = (level) => (level & 128 ? (level & 127) + 1 : 127 - level);

/** Convenience wrapper around the browser interface for audio recording. */
export class AudioRecorder {
    /**
     * Constructs a new AudioRecorder instance, which is a convenience wrapper around the browser's
     * audio streaming interface.
     *
     * @param options.audioInputDeviceId The audio device to use for recording.  If unspecified, the
     * default audio device from the browser will be used.
     * @param options.levelCheckIntervalMs The number of milliseconds between volume level checks.
     * Defaults to 100 ms.
     * @param options.maxRecordingTimeSecs The maximum number of seconds to record for.  Defaults to
     * 10 minutes.
     * @param options.silenceThreshold For purposes of detecting "silent" recordings, audio levels
     * below this threshold will be considered silence, on a scale from 0 to 1.  Defaults to 0.1.
     * @param options.silenceTimeoutSecs If this many seconds of silence pass by after the recording
     * starts, the 'silence' event will be triggered.  Defaults to 5 seconds.
     * @param options.audibleThresholdPercentage Minimal percentage of audible in recorded sample
     */
    constructor(options = {}) {
        this._emitter = mitt();
        this._levelCheckIntervalMs = options.levelCheckIntervalMs ?? DEFAULT_LEVEL_CHECK_INTERVAL_MS;
        this._silenceThreshold = options.silenceThreshold ?? DEFAULT_SILENCE_THRESHOLD;
        this._maxRecordingTimeSecs = options.maxRecordingTimeSecs ?? DEFAULT_MAX_RECORDING_TIME_SECS;
        this._audioInputDeviceId = options.audioInputDeviceId ?? localStorage.getItem("valence_preferred_microphone") ?? null;
        this._silenceTimeoutSecs = options.silenceTimeoutSecs ?? DEFAULT_SILENCE_TIMEOUT_SECS;
        this._audibleThresholdPercentage = options.audibleThresholdPercentage ?? DEFAULT_AUDIBLE_THRESHOLD_PERCENTAGE;
        this._initTimeoutMs = options.initTimeoutMs ?? DEFAULT_INIT_TIMEOUT_MS;
        this._slowInitThresholdMs = options.slowInitThresholdMs ?? DEFAULT_SLOW_INIT_THRESHOLD_MS;
        this._isInitializing = false;
        this._abortController = null;

        // It's OK to call this synchronously here because the async bits won't be called on a new
        // instance.
        this._reset();
    }

    async _reset() {
        this._isRecording = false;
        this._isSilent = true;
        this._isAborted = false;
        this._startTime = null;
        this._endTime = null;
        this._currentLevel = 0;
        this._chunks = [];
        this._lastLevelCheckTime = null;
        this._levelSum = 0;
        this._levelCount = 0;
        this._peakLevel = 0;
        this._audibleCount = 0;
        this._maxTimeTimeout = null;
        this._levelsArray = null;
        this._mimeType = null;
        this._streamStabilized = false;
        this._warmupStartTime = null;
        this._warmupMs = null;

        this._stream = null;
        this._recorder = null;

        if (this._abortController) {
            this._abortController.abort();
            this._abortController = null;
        }
        this._isInitializing = false;

        if (this._silenceTimeout) {
            clearTimeout(this._silenceTimeout);
            this._silenceTimeout = null;
        }

        if (this._maxTimeTimeout) {
            clearTimeout(this._maxTimeTimeout);
            this._maxTimeTimeout = null;
        }

        if (this._checkAudioLevelTimeout) {
            clearTimeout(this._checkAudioLevelTimeout);
            this._checkAudioLevelTimeout = null;
        }

        if (this._warmupTimeout) {
            clearTimeout(this._warmupTimeout);
            this._warmupTimeout = null;
        }

        if (this._analyzer) {
            this._analyzer.disconnect();
        }
        this._analyzer = null;

        if (this._source) {
            this._source.disconnect();
        }
        this._source = null;

        if (this._context) {
            await this._context.close();
        }
        this._context = null;
    }

    /**
     * Initializes the recorder by setting up the media stream and audio context.
     * This can be called before start() to pre-warm the recording pipeline.
     * Safe to call multiple times - idempotent.
     *
     * @param {function} failureCallback(micPermission.state) A callback to be called if initialization fails due to microphone permissions.
     */
    async initialize(failureCallback = () => null) {
        // Already initialized and ready
        if (this._stream && this._recorder && !this._isInitializing) {
            return;
        }

        // Stream alive but recorder cleaned up — recreate cheaply
        if (this._stream && !this._recorder && !this._isInitializing) {
            this._recreateRecorder();
            this._emitter.emit("initialized");
            return;
        }

        // Already initializing
        if (this._isInitializing) {
            return;
        }

        if (!navigator.mediaDevices?.getUserMedia) {
            const error = new Error("Could not initialize recording: no user media devices");
            this._emitter.emit("initialization-failed", { error });
            throw error;
        }

        this._isInitializing = true;
        this._emitter.emit("initializing");

        try {
            // Create timeout protection for getUserMedia
            let timeoutId;
            const timeoutPromise = new Promise((_, reject) => {
                timeoutId = setTimeout(() => {
                    reject(new Error("Microphone initialization timed out"));
                }, this._initTimeoutMs);
            });

            const initStartTime = performance.now();
            const hasPreferredDevice = Boolean(this._audioInputDeviceId);
            try {
                const requestStream = (audioConstraints) => {
                    return Promise.race([navigator.mediaDevices.getUserMedia({ audio: audioConstraints }), timeoutPromise]);
                };

                try {
                    this._stream = await requestStream(this._audioInputDeviceId ? { deviceId: this._audioInputDeviceId } : true);
                } catch (err) {
                    const shouldRetryWithDefaultDevice = hasPreferredDevice && (err.name === "NotFoundError" || err.name === "OverconstrainedError");

                    if (!shouldRetryWithDefaultDevice) {
                        throw err;
                    }

                    console.warn("Preferred microphone unavailable, retrying with default microphone", err);
                    this._audioInputDeviceId = null;
                    localStorage.removeItem("valence_preferred_microphone");
                    this._stream = await requestStream(true);
                }

                clearTimeout(timeoutId);
                const durationMs = Math.round(performance.now() - initStartTime);
                if (durationMs >= this._slowInitThresholdMs) {
                    logUserInteraction("mic_init_slow", {
                        duration_ms: durationMs,
                        threshold_ms: this._slowInitThresholdMs,
                        timed_out: false,
                    });
                }
            } catch (err) {
                clearTimeout(timeoutId);
                if (err.message === "Microphone initialization timed out") {
                    const durationMs = Math.round(performance.now() - initStartTime);
                    logUserInteraction("mic_init_slow", {
                        duration_ms: durationMs,
                        threshold_ms: this._slowInitThresholdMs,
                        timed_out: true,
                        timeout_ms: this._initTimeoutMs,
                    });
                    console.error("AudioRecorder initialization timed out", err);
                    this._isInitializing = false;
                    this._emitter.emit("initialization-failed", { error: err });
                    await failureCallback("timeout");
                    throw err;
                }
                console.error("AudioRecorder failed to get media stream", err);
                this._isInitializing = false;
                this._emitter.emit("initialization-failed", { error: err });
                try {
                    const micPermission = await navigator.permissions.query({ name: "microphone" });
                    await failureCallback(micPermission.state);
                } catch (_) {
                    // Permissions API not available in all browsers
                }
                throw err;
            }

            this._context = new AudioContext();
            await this._context.resume();
            this._source = this._context.createMediaStreamSource(this._stream);
            this._analyzer = this._context.createAnalyser();
            this._analyzer.fftSize = 256;
            this._levelsArray = new Uint8Array(this._analyzer.fftSize);
            this._source.connect(this._analyzer);

            const recorderOptions = {};
            if (MediaRecorder.isTypeSupported(PREFERRED_MIME_TYPE)) {
                recorderOptions.mimeType = PREFERRED_MIME_TYPE;
                recorderOptions.audioBitsPerSecond = AUDIO_BITS_PER_SECOND;
            } else {
                console.warn("No optimized codec available, using default audio codec.");
            }

            try {
                this._recorder = new MediaRecorder(this._stream, recorderOptions);
            } catch (err) {
                console.error("AudioRecorder could not instantiate MediaRecorder", err);
                this._isInitializing = false;
                this._emitter.emit("initialization-failed", { error: err });
                throw err;
            }

            this._mimeType = this._recorder.mimeType;

            if (!recorderOptions.mimeType) {
                logUserInteraction("audio_codec_fallback", { default_mime_type: this._mimeType }).catch(() => {});
            }

            this._isInitializing = false;
            this._emitter.emit("initialized");
        } catch (err) {
            this._isInitializing = false;
            this._emitter.emit("initialization-failed", { error: err });
            throw err;
        }
    }

    _isStreamDead() {
        if (!this._stream) {
            return true;
        }
        const tracks = this._stream.getAudioTracks();
        return tracks.length === 0 || tracks.every((t) => t.readyState === "ended");
    }

    /**
     * Recreates the MediaRecorder from the existing stream.
     * Used when the stream is still alive but the recorder was cleaned up after a previous recording.
     */
    _recreateRecorder() {
        const recorderOptions = {};
        if (MediaRecorder.isTypeSupported(PREFERRED_MIME_TYPE)) {
            recorderOptions.mimeType = PREFERRED_MIME_TYPE;
            recorderOptions.audioBitsPerSecond = AUDIO_BITS_PER_SECOND;
        }

        this._recorder = new MediaRecorder(this._stream, recorderOptions);
        this._mimeType = this._recorder.mimeType;
    }

    /**
     * Starts a new recording.
     *
     * Recording may not start immediately.  To ensure audio is actually being recorded, either
     * `await` this function, or subscribe to the `start` event.
     *
     * @param {function} failureCallback(micPermission.state) A callback to be called if the recording fails to start due to microphone permissions. The argument is the state of the microphone permission.
     */
    async start(failureCallback = () => null) {
        if (this._isRecording) {
            return;
        }

        if (this._stream && this._isStreamDead()) {
            this.releaseStream();
        }

        if (!this._stream) {
            await this.initialize(failureCallback);
        } else if (!this._recorder) {
            this._recreateRecorder();
        }

        this._dataAvailableListener = this._recorder.addEventListener("dataavailable", (e) => this._handleDataAvailable(e));

        this._isRecording = true;
        this._isSilent = true;
        this._isAborted = false;
        this._startTime = new Date();
        this._endTime = null;
        this._currentLevel = 0;
        this._chunks = [];
        this._streamStabilized = false;
        this._levelSum = 0;
        this._levelCount = 0;
        this._peakLevel = 0;
        this._audibleCount = 0;
        this._warmupStartTime = performance.now();
        this._warmupMs = null;

        try {
            this._recorder.start();
        } catch (err) {
            this._isRecording = false;
            this._startTime = null;
            this._recorder?.removeEventListener("dataavailable", this._dataAvailableListener);
            this._dataAvailableListener = null;
            this._recorder = null;
            throw err;
        }

        this._lastLevelCheckTime = performance.now();
        this._checkAudioLevelTimeout = setTimeout(() => this._checkAudioLevel(), this._levelCheckIntervalMs);
        this._silenceTimeout = setTimeout(() => this._checkSilence(), this._silenceTimeoutSecs * 1000);
        this._maxTimeTimeout = setTimeout(() => this._handleMaxTimeTimeout(), this._maxRecordingTimeSecs * 1000);

        // Safety net: force-stabilize after warmup timeout if no audio arrives
        this._warmupTimeout = setTimeout(() => {
            if (!this._streamStabilized && this._isRecording) {
                this._streamStabilized = true;
                this._warmupMs = DEFAULT_WARMUP_TIMEOUT_MS;
            }
        }, DEFAULT_WARMUP_TIMEOUT_MS);

        this._emitter.emit("start");
    }

    /**
     * Stops the active recording.  Even though recording has stopped, audio data will not be
     * available right away.  To get the recorded audio, listen for the subsequent `finish` event.
     *
     * @param options.abort If `true`, recorded audio will be discarded and no `finish` event will
     * be triggered.  Defaults to `false`.
     */
    stop(options = {}) {
        if (!this._isRecording) {
            return;
        }

        this._isRecording = false;

        this._isAborted = options.abort ?? false;
        if (this._isAborted) {
            this._startTime = null;

            if (this._dataAvailableListener) {
                this._recorder?.removeEventListener("dataavailable", this._dataAvailableListener);
                this._dataAvailableListener = null;
            }
        }

        if (this._silenceTimeout) {
            clearTimeout(this._silenceTimeout);
            this._silenceTimeout = null;
        }

        if (this._maxTimeTimeout) {
            clearTimeout(this._maxTimeTimeout);
            this._maxTimeTimeout = null;
        }

        if (this._checkAudioLevelTimeout) {
            clearTimeout(this._checkAudioLevelTimeout);
            this._checkAudioLevelTimeout = null;
        }

        if (this._warmupTimeout) {
            clearTimeout(this._warmupTimeout);
            this._warmupTimeout = null;
        }

        if (this._recorder) {
            this._recorder.stop();
        }

        this._endTime = new Date();

        this._emitter.emit("stop", { isAborted: this._isAborted, isTimeout: !!options.isTimeout });
    }

    async _handleDataAvailable({ data }) {
        if (this._isAborted) {
            return;
        }

        this._chunks.push(data);

        this._emitter.emit("data-available", {
            data,
            mimeType: this._mimeType,
            isFinal: !this._isRecording,
        });

        if (this._isRecording) {
            return;
        }

        this._recorder?.removeEventListener("dataavailable", this._dataAvailableListener);
        this._recorder = null;

        const recording = new Blob(this._chunks, { type: this._mimeType });

        const audiblePercentage = this._levelCount > 0 ? this._audibleCount / this._levelCount : 0;
        const avgLevel = this._levelCount > 0 ? this._levelSum / this._levelCount : 0;
        const peakLevel = this._peakLevel;

        if (audiblePercentage >= this._audibleThresholdPercentage) {
            this._emitter.emit("finish", {
                blob: recording,
                lengthMs: this.recordingTimeMs,
                audioStats: { audiblePercentage, avgLevel, peakLevel, sampleCount: this._levelCount, warmupMs: this._warmupMs },
            });
        } else {
            this._emitter.emit("silence");
        }
    }

    _checkAudioLevel() {
        if (!this._isRecording) {
            return;
        }

        this._analyzer.getByteTimeDomainData(this._levelsArray);
        const peakLevel = maxBy(this._levelsArray, absLevel);

        this._currentLevel = absLevel(peakLevel) / 128.0;

        // Skip recording zero-level samples to history until the stream stabilizes
        if (!this._streamStabilized) {
            if (this._currentLevel > WARMUP_LEVEL_THRESHOLD) {
                this._streamStabilized = true;
                this._warmupMs = Math.round(performance.now() - this._warmupStartTime);
                if (this._warmupTimeout) {
                    clearTimeout(this._warmupTimeout);
                    this._warmupTimeout = null;
                }
                // Restart silence timeout from this moment
                if (this._silenceTimeout) {
                    clearTimeout(this._silenceTimeout);
                }
                this._silenceTimeout = setTimeout(() => this._checkSilence(), this._silenceTimeoutSecs * 1000);
            }
            // Don't record zero-level samples — they'd pollute audible percentage
        }

        if (this._streamStabilized) {
            this._levelCount++;
            this._levelSum += this._currentLevel;
            this._peakLevel = Math.max(this._peakLevel, this._currentLevel);
            if (this._currentLevel >= this._silenceThreshold) {
                this._audibleCount++;
            }
        }

        if (this._isSilent && this._currentLevel >= this._silenceThreshold) {
            this._isSilent = false;
        }

        this._lastLevelCheckTime = performance.now();
        setTimeout(() => this._checkAudioLevel(), this._levelCheckIntervalMs);

        this._emitter.emit("level", this._currentLevel);
    }

    _checkSilence() {
        if (!this.isRecording) {
            return;
        }

        if (this._isSilent) {
            this._emitter.emit("silence");
        }
    }

    _handleMaxTimeTimeout() {
        if (!this.isRecording) {
            return;
        }

        this.stop({ isTimeout: true });
    }

    /**
     * Adds an event handler to this instance for a given event.
     *
     * Events:
     * - `start` - Published when recording is started.
     * - `level` - Published whenever new volume level data is available.  The argument is the
     * current volume level, on a scale from 0 to 1.
     * - `silence` - Published if `silenceTimeoutSecs` seconds have elapsed since the recording
     * started and the recording so far only contains silence.
     * - `data-available` - Published multiple times during a recording whenever a chunk of audio
     * data becomes available.  You can handle this event to receive chunks of audio data as the
     * recording progresses, or you can just wait for the final `finish` event to get everything at
     * once.  The argument is an object with three fields: `data`, `mimeType`, and `isFinal`.  If
     * `isFinal` is `true`, then recording has stopped and there will be no further `data-available`
     * events for this recording.
     * - `stop` - Published when a recording is stopped for any reason.  The argument is an object
     * with two fields: `isAborted` indicates that the recording was aborted, while `isTimeout`
     * indicates that the recording stopped due to a timeout.  Audio data is not immediately
     * available after a recording stops.  If you want the audio data, listen for the subsequent
     * `finish` event.
     * - `finish` - Published when a recording has completed and the audio data is available.  The
     * argument is an object with two properties: `blob` is a `Blob` containing the audio data, and
     * `lengthMs` is the length of the recording in ms.  Note that this event will not be published
     * if the recording was aborted.
     */
    get on() {
        return this._emitter.on;
    }

    /** Removes an event handler for a given even. */
    get off() {
        return this._emitter.off;
    }

    /** True if this instance is actively recording. */
    get isRecording() {
        return this._isRecording;
    }

    /** True if the recorder is currently initializing. */
    get isInitializing() {
        return this._isInitializing;
    }

    /** True if the active (or most recently completed) recording contains only silence. */
    get isSilent() {
        return this._isSilent;
    }

    /** The length of the current or most recent recording, in ms. */
    get recordingTimeMs() {
        if (!this._startTime) {
            return 0;
        }
        if (!this._endTime) {
            return new Date() - this._startTime;
        }
        return this._endTime - this._startTime;
    }

    /** The current audio level, on a scale from 0 to 1. */
    get level() {
        if (!this._isRecording) {
            return 0;
        }
        return this._currentLevel;
    }

    /** The MIME type of the current or most recent recording. */
    get mimeType() {
        return this._mimeType;
    }

    /**
     * Sets the device ID for audio recording.  This change does not take effect until the next
     * recording is started.
     */
    setAudioInputDeviceId(id) {
        this._audioInputDeviceId = id;
    }

    isBelowSilenceThreshold(level) {
        return level <= this._silenceThreshold;
    }

    /**
     * Releases the microphone stream, stopping all tracks.
     * Safe to call at any time. No-op if no stream is active.
     * After calling this, the next recording will need a fresh getUserMedia() call.
     */
    releaseStream() {
        if (this._isRecording) {
            return;
        }

        if (this._stream) {
            this._stream.getTracks().forEach((track) => track.stop());
            this._stream = null;
        }

        this._recorder = null;

        if (this._analyzer) {
            this._analyzer.disconnect();
            this._analyzer = null;
        }

        if (this._source) {
            this._source.disconnect();
            this._source = null;
        }

        if (this._context) {
            this._context.close();
            this._context = null;
        }

        this._emitter.emit("released");
    }
}

/**
 * Formats a time duration from milliseconds to mm:ss or hh:mm:ss format.
 *
 * @param {number} duration
 * @returns The time duration in mm:ss or hh:mm:ss format.
 */
export function msToDisplayTime(duration) {
    // Split
    let seconds = Math.floor((duration / 1000) % 60);
    let minutes = Math.floor((duration / (1000 * 60)) % 60);
    let hours = Math.floor((duration / (1000 * 60 * 60)) % 24);

    // Zero-pad
    let hours_str = hours < 10 ? "0" + hours : hours;
    let minutes_str = minutes < 10 ? "0" + minutes : minutes;
    let seconds_str = seconds < 10 ? "0" + seconds : seconds;

    if (hours > 0) {
        return hours_str + ":" + minutes_str + ":" + seconds_str;
    }
    return minutes_str + ":" + seconds_str;
}
